import React from 'react';
import NProgress from 'nprogress';
import { toast } from 'react-toastify';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { useRouter } from 'next/router';
import { v4 as uuid } from 'uuid';
import { logEvent } from 'firebase/analytics';
import { ZodError } from 'zod';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { firestore, loadAnalytics } from '../../../lib';
import { Application, peopleSchema, Person } from '../../../models';
import { Button } from '../../commons';
import PersonForm from './PersonForm';

const person1 = uuid();
const person2 = uuid();

interface StaticalFormProps {
  application?: Application;
  people: Person[];
  isReadOnly?: boolean;
}

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function PeopleForm({
  application,
  people: _people,
  isReadOnly = false,
}: StaticalFormProps) {
  const router = useRouter();
  const [people, setPeople] = React.useState<Person[]>([]);

  React.useEffect(() => {
    if (_people && Array.isArray(_people) && _people.length > 0) {
      setPeople(_people);
    } else {
      setPeople([
        {
          id: person1,
          name: '',
          position: '',
          institution: '',
          address: '',
          phone: '',
        },
        {
          id: person2,
          name: '',
          position: '',
          institution: '',
          address: '',
          phone: '',
        },
      ]);
    }
  }, [_people]);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let data: any = null;
    try {
      data = peopleSchema.parse(people);
    } catch (err) {
      if (err instanceof ZodError) {
        return toastError(err.issues[0].message);
      }
      return toastError(
        'An error has occurred, please try again later. CODE: E81'
      );
    }
    try {
      NProgress.start();
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '8',
        people: data,
      });
      logEvent(loadAnalytics(), 'step_completed', {
        step: '8',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '8',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog?.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/9'
      );
    } catch (err) {
      NProgress.done();
      posthog?.captureException(err);
      Sentry.addBreadcrumb({
        category: 'step-8',
        level: 'error',
      });
      Sentry.captureException(err);
      toastError('An error has occurred, please try again later. CODE: E82');
    }
  };
  return (
    <form className="mt-8 w-full" method="post" onSubmit={onSubmit}>
      <p className="font-semibold text-2xl text-neutral-500 mt-2">Person 1</p>
      <PersonForm
        id={people?.[0]?.id}
        person={people[0]}
        setPeople={setPeople}
      />
      <p className="font-semibold text-2xl text-neutral-500 mt-2">Person 2</p>
      <PersonForm
        id={people?.[1]?.id}
        person={people[1]}
        setPeople={setPeople}
      />
      {isReadOnly ? null : (
        <div className="mt-8 flex">
          <Button type="submit" color="blue" className="ml-auto w-[135px]">
            Continue
          </Button>
        </div>
      )}
    </form>
  );
}
